import { useMatrixEtaContext, useMatrixSettingsContext } from '@app/components/products/ProductMatrix/context';
import { Eta } from '@app/components/various/Icons';
import { Tooltip } from '@app/components/various/Tooltip';
import { Trend } from '@app/components/various/Trend/Trend';
import { cn } from '@app/utils/cn';
import { useIsTouchScreen } from '@app/utils/hooks/useIsTouchScreen';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { ItemEtasList } from '@typings';
import React from 'react';

import { EtaTooltipContent } from './EtaTooltipContent';
import { TrendTooltipLine } from './TrendTooltipLine';

interface Props {
  value: number;
  quantityBefore: number | undefined;
  color?: string | undefined;
  itemEtas?: ItemEtasList;
  isDistributionPreview: boolean;
  isDisabled?: boolean;
}

export const QuantityCellInfo = ({
  children,
  value,
  quantityBefore,
  color,
  itemEtas,
  isDistributionPreview,
  isDisabled,
}: React.WithChildren<Props>) => {
  const { isEtaVisible } = useMatrixEtaContext();
  const isQuantityChanged = isDefined(quantityBefore) && quantityBefore !== value;
  const isTouchScreen = useIsTouchScreen();
  const removedQuantity = isDefined(quantityBefore) && quantityBefore > value ? quantityBefore - value : undefined;
  const { isOrderEditable } = useMatrixSettingsContext();

  const addedQuantity = isDefined(quantityBefore) && quantityBefore < value ? value - quantityBefore : undefined;

  const shouldShowItemEtas = isDefined(itemEtas) && !isEmpty(itemEtas);

  const tooltipContent = (isQuantityChanged || shouldShowItemEtas) && (
    <>
      {shouldShowItemEtas && <EtaTooltipContent itemEtas={itemEtas} />}
      {isQuantityChanged && <TrendTooltipLine removedQuantity={removedQuantity} addedQuantity={addedQuantity} />}
    </>
  );

  if (isDistributionPreview) {
    return (
      <>
        <div className="h-full w-full">
          {children}
          {isQuantityChanged && isOrderEditable && (
            <div className="absolute bottom-0 left-0 pl-0.5">
              <Trend type={removedQuantity ? 'down' : 'up'} />
            </div>
          )}
        </div>
      </>
    );
  }

  const isEtaColored = isEtaVisible || (isTouchScreen && !isDisabled);

  return (
    <Tooltip content={tooltipContent}>
      <div
        className={cn('h-full w-full', {
          'cursor-not-allowed': isDisabled,
        })}
      >
        {children}
        {isQuantityChanged && isOrderEditable && (
          <div className="absolute bottom-0 left-0 flex items-center overflow-hidden px-0.5 pb-0.5">
            <span className="heading-4">{quantityBefore}</span>
            <Trend type={removedQuantity ? 'down' : 'up'} />
          </div>
        )}
        {shouldShowItemEtas && (
          <span className="absolute left-0.5 top-0.5 inline-flex" style={isEtaColored ? { color } : {}}>
            <Eta className="size-3" />
            <div
              className={cn('absolute top-0 h-full w-full rounded-small bg-transparent opacity-15 transition-colors', {
                'bg-current': isEtaColored,
              })}
            />
          </span>
        )}
      </div>
    </Tooltip>
  );
};
